import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet } from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useVoiceMode } from '@src/screens/Nora/hooks/useVoiceMode'
import { useNoraVoiceModeVisualContext } from '@src/screens/Nora/context/NoraVoiceModeContext'
import { NoraVoicePlayer } from './NoraVoicePlayer'
import { BottomHorizontalGlow } from './BottomHorizontalGlow'
import { AmbientGlowBackground } from './AmbientGlowBackground'

interface NoraVoiceModeRuntimeProps {
  conversationId?: string
}

export type NoraVoiceGlowMode = 'hidden' | 'listening' | 'nora-speaking'

export const NORA_VOICE_GLOW_TRANSITION_MS = 200

const GLOW_OPACITY_TIMING = {
  duration: NORA_VOICE_GLOW_TRANSITION_MS,
  easing: Easing.inOut(Easing.ease),
}

interface NoraVoiceGlowState {
  enabled: boolean
  isSpeaking: boolean
  muted: boolean
}

export const resolveNoraVoiceGlowMode = ({
  enabled,
  isSpeaking,
  muted,
}: NoraVoiceGlowState): NoraVoiceGlowMode => {
  if (!enabled) {
    return 'hidden'
  }
  if (isSpeaking) {
    return 'nora-speaking'
  }
  return muted ? 'hidden' : 'listening'
}

export const NoraVoiceModeRuntime = ({ conversationId }: NoraVoiceModeRuntimeProps) => {
  const { currentAudioPath, onPlaybackEnd, onPlaybackError } = useVoiceMode({
    conversationId,
  })

  return (
    <NoraVoicePlayer
      currentAudioPath={currentAudioPath}
      onPlaybackEnd={onPlaybackEnd}
      onPlaybackError={onPlaybackError}
    />
  )
}

// Rendered separately from the runtime so the screen can mount it *before* the
// chat in the tree: React Native has no cross-sibling z-index, so paint order
// is the z-order. The chat renders with transparent surfaces (see
// ChatScreen's transparentBackground), letting this wave show through behind
// the messages. Reads speaking state from context rather than calling
// useVoiceMode, which would start a second mic/playback session.
export const NoraVoiceModeBackground = () => {
  const {
    enabled: voiceModeEnabled,
    muted,
    speakingState: { isSpeaking, userSpeaking },
    inputLevel,
  } = useNoraVoiceModeVisualContext()
  const [surfaceMounted, setSurfaceMounted] = useState(voiceModeEnabled)
  const generationRef = useRef(0)
  const animationFrameRef = useRef<number | null>(null)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const visualMode = resolveNoraVoiceGlowMode({
    enabled: voiceModeEnabled,
    isSpeaking,
    muted,
  })
  const bottomGlowOpacity = useSharedValue(visualMode === 'listening' ? 1 : 0)
  const noraSpeakingGlowOpacity = useSharedValue(visualMode === 'nora-speaking' ? 1 : 0)

  /* eslint-disable react-hooks/exhaustive-deps -- Reanimated shared values are stable refs
  and must not be both effect dependencies and mutation targets. */
  useEffect(() => {
    bottomGlowOpacity.value = withTiming(visualMode === 'listening' ? 1 : 0, GLOW_OPACITY_TIMING)
    noraSpeakingGlowOpacity.value = withTiming(
      visualMode === 'nora-speaking' ? 1 : 0,
      GLOW_OPACITY_TIMING,
    )
  }, [visualMode])
  /* eslint-enable react-hooks/exhaustive-deps */

  const bottomGlowStyle = useAnimatedStyle(() => ({ opacity: bottomGlowOpacity.value }))
  const noraSpeakingGlowStyle = useAnimatedStyle(() => ({ opacity: noraSpeakingGlowOpacity.value }))

  useEffect(() => {
    generationRef.current += 1
    const generation = generationRef.current

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (exitTimerRef.current !== null) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }

    if (voiceModeEnabled) {
      if (!surfaceMounted) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = null
          if (generationRef.current === generation) {
            setSurfaceMounted(true)
          }
        })
      }
      return undefined
    }

    if (surfaceMounted) {
      exitTimerRef.current = setTimeout(() => {
        exitTimerRef.current = null
        if (generationRef.current === generation) {
          setSurfaceMounted(false)
        }
      }, NORA_VOICE_GLOW_TRANSITION_MS)
    }

    return undefined
  }, [surfaceMounted, voiceModeEnabled])

  useEffect(
    () => () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (exitTimerRef.current !== null) {
        clearTimeout(exitTimerRef.current)
      }
    },
    [],
  )

  if (!surfaceMounted) {
    return null
  }

  return (
    <>
      <Animated.View
        testID="bottom-horizontal-glow-layer"
        pointerEvents="none"
        style={[styles.glowLayer, bottomGlowStyle]}
      >
        <BottomHorizontalGlow inputLevel={inputLevel} userSpeaking={userSpeaking} />
      </Animated.View>
      <Animated.View
        testID="nora-speaking-glow-layer"
        pointerEvents="none"
        style={[styles.glowLayer, noraSpeakingGlowStyle]}
      >
        <AmbientGlowBackground />
      </Animated.View>
    </>
  )
}

const styles = StyleSheet.create({
  glowLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
})
